import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { compareVersions } from '@teaplus/shared';
import { API_EVENTS, api } from './api';

export interface UpdateState {
  supported: boolean;
  current: string;
  status:
    | 'idle'
    | 'checking'
    | 'up-to-date'
    | 'available'
    | 'downloading'
    | 'ready'
    | 'reinstalling'
    | 'reinstall-ready'
    | 'error';
  detail?: string;
  version: string | null;
  notes: string | null;
  progress: number | null;
  error: string | null;
}

export interface UpdatesBridge {
  getState: () => Promise<UpdateState | null>;
  check: () => Promise<UpdateState>;
  download: () => Promise<UpdateState>;
  install: () => Promise<boolean>;
  reinstall: () => Promise<UpdateState>;
  onChange: (cb: (s: UpdateState) => void) => () => void;
}

export const updatesBridge = (): UpdatesBridge | null => window.teaplus?.updates ?? null;

/**
 * True when the server says this desktop copy is too old to use (MIN_DESKTOP_VERSION). Checked at
 * start, every half hour, and whenever an API call comes back UPDATE_REQUIRED.
 */
export function useUpdateRequired() {
  const u = useAppUpdates();
  const q = useQuery({
    queryKey: ['system', 'requirements'],
    queryFn: () => api<{ minDesktopVersion: string }>('/api/system/requirements'),
    enabled: !!u,
    staleTime: 30 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
  });
  const { refetch } = q;
  useEffect(() => {
    const on = () => void refetch();
    window.addEventListener(API_EVENTS.updateRequired, on);
    return () => window.removeEventListener(API_EVENTS.updateRequired, on);
  }, [refetch]);
  const min = q.data?.minDesktopVersion;
  return !!u && !!min && compareVersions(u.state.current, min) < 0
    ? { ...u, min, recheck: refetch }
    : null;
}

export function useAppUpdates() {
  const bridge = updatesBridge();
  const [state, setState] = useState<UpdateState | null>(null);
  useEffect(() => {
    if (!bridge) return;
    void bridge.getState().then((s) => s && setState(s));
    return bridge.onChange(setState);
  }, [bridge]);
  return bridge && state ? { ...bridge, state } : null;
}
